import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {StarIcon} from './StarIcon';
import {TextStyle, TextContainer} from '@shopify/polaris';

export const Classes = {
  container: 'star-rating',
  containerReadOnly: 'star-rating--read-only',
  inputContainer: 'star-rating__input-container',
  inputGroup: 'star-rating__input-group',
  input: 'star-rating__input',
  inputControl: 'star-rating__input__control',
  inputLabel: 'star-rating__input__label',
  star: 'star-rating__star',
  starFilled: 'star-rating__star--filled',
  icon: 'star-rating__star__icon',
  size: 'star-rating__star__icon-size',
};

export default class StarRating extends React.Component {
  static defaultProps = {
    readOnly: false,
    value: null,
    size: 16,
    onChange: null,
    onHover: null,
  };

  constructor(props) {
    super(props);

    this.onInputChange = this.onInputChange.bind(this);
  }

  onInputChange(value) {
    const {onChange} = this.props;

    return () => onChange(value);
  }

  onInputHover(idx) {
    const {onHover} = this.props;

    return () => onHover(idx);
  }

  renderFieldset() {
    const {size, name, title, value} = this.props;
    const legendElement = `${name}__legend`;

    return (
      <fieldset className={Classes.container} aria-describedby={legendElement}>
        <legend id={legendElement}>{title}</legend>
        <div className={Classes.inputContainer}>
          <ul className={Classes.inputGroup}>
            {Array.from(Array(5)).map((_, idx) => {
              const key = `${name}-${idx}`;
              const inputValue = idx + 1;
              const fillPercentage = inputValue > value ? 0 : 100;
              const labelSuffix = idx === 0 ? 'Star' : 'Stars';

              return (
                <li // eslint-disable-line jsx-a11y/no-noninteractive-element-interactions
                  key={key}
                  className={Classes.input}
                  onMouseEnter={this.onInputHover(idx + 1)}
                  onMouseLeave={this.onInputHover(undefined)}
                >
                  <label htmlFor={`${name}-input-${inputValue}`}>
                    <span className={Classes.inputControl}>
                      <StarIcon
                        fillPercentage={fillPercentage}
                        className={classNames(
                          Classes.icon,
                          `${Classes.size}-${size}`
                        )}
                      />
                      <input
                        id={`${name}-input-${inputValue}`}
                        name={name}
                        type="radio"
                        value={inputValue}
                        onChange={this.onInputChange(inputValue)}
                      />
                    </span>
                    <span className={Classes.inputLabel}>
                      {inputValue} {labelSuffix}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      </fieldset>
    );
  }

  renderReadOnlyStars({value, size}) {
    return (
      <span className={Classes.containerReadOnly}>
        {Array.from(Array(5)).map((_, idx) => {
          const key = `rating-${idx}`;
          return <Star value={value} index={idx} key={key} size={size} />;
        })}
      </span>
    );
  }

  renderReadOnly() {
    const {value, title, size} = this.props;

    return title ? (
      <TextContainer spacing="tight">
        <TextStyle variation="strong">{title}</TextStyle>
        {this.renderReadOnlyStars({value, size})}
      </TextContainer>
    ) : (
      this.renderReadOnlyStars({value, size})
    );
  }

  render() {
    return this.props.readOnly ? this.renderReadOnly() : this.renderFieldset();
  }
}

StarRating.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  size: PropTypes.number,
  name: PropTypes.string,
  title: PropTypes.string,
  readOnly: PropTypes.bool,
  onChange: PropTypes.func,
  onHover: PropTypes.func,
};

function Star({value, index, size}) {
  let fillPercentage;
  const flooredValue = Math.floor(value);
  const ceilingValue = Math.ceil(value);

  if (index + 1 <= flooredValue) {
    fillPercentage = 100;
  } else if (index + 1 > ceilingValue) {
    fillPercentage = 0;
  } else {
    fillPercentage = (value - flooredValue) * 100;
  }

  return (
    <span className={Classes.Star}>
      <StarIcon
        fillPercentage={fillPercentage}
        className={classNames(Classes.icon, `${Classes.size}-${size}`)}
      />
    </span>
  );
}

Star.propTypes = {
  index: PropTypes.number,
  value: PropTypes.number,
  size: PropTypes.number,
};
